import React, { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { FormatListNumbered } from '@mui/icons-material';
import { useActive } from '@remirror/react';
import { useCommands, useRemirrorContext } from '@remirror/react-core';
import { Toolbar, VerticalDivider } from '@remirror/react-ui';

import { Alignment } from './components/Alignment';
import { Heading } from './components/Heading';
import { ImageInputDialog } from './components/Image';
import { FloatingLinkToolbar } from './components/Link';

import '../styles.css';

interface IMenuProps {
  bold?: boolean;
  italic?: boolean;
  image?: boolean;
  link?: boolean;
  alignment?: boolean;
  orderedList?: boolean;
  heading?: number;
}

export const VisualEditorMenuBar: React.FC<IMenuProps> = ({
  bold = true,
  italic = true,
  image = true,
  link = true,
  alignment = false,
  orderedList = true,
  heading = true
}) => {
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  const { toggleBold, focus, toggleItalic, toggleOrderedList } = useCommands();
  const active = useActive();

  const { attrs } = useRemirrorContext();

  const currentLink = useMemo(() => attrs.link(), [attrs.link()]);

  const currentHeadingLevel: number = useMemo(
    () => Number(attrs.heading()?.level) || 0,
    [attrs.heading()?.level]
  );

  return (
    <div className={'menu-bar-wrapper sticky top-0 bg-inherit py-4  z-10'}>
      <Toolbar
        className={
          'menu-bar flex h-[57px] justify-between items-center shrink-0 rounded-md shadow-md p-2.5 sticky top-2 right-3 left-3 w-full'
        }
        style={{ zIndex: 9 }}
      >
        <FloatingLinkToolbar
          isDialogOpen={isLinkDialogOpen}
          setIsDialogOpen={setIsLinkDialogOpen}
        />
        {bold && (
          <button
            onClick={() => {
              toggleBold();
              focus();
            }}
            className={active.bold() ? 'is-active' : ''}
            type="button"
          >
            B
          </button>
        )}
        {italic && (
          <button
            onClick={() => {
              toggleItalic();
              focus();
            }}
            className={active.italic() ? 'is-active italic' : 'italic'}
            type="button"
          >
            I
          </button>
        )}
        <VerticalDivider />
        {image && <ImageInputDialog />}
        {link && (
          <button
            onClick={() => setIsLinkDialogOpen((prev) => !prev)}
            className={currentLink ? 'is-active' : ''}
            type="button"
          >
            <FontAwesomeIcon className={'fa-solid fa-link-horizontal'} />
          </button>
        )}
        {alignment && <Alignment />}
        {orderedList && (
          <button
            onClick={() => toggleOrderedList()}
            className={active.orderedList() ? 'is-active' : ''}
            type="button"
            disabled={currentHeadingLevel > 0}
          >
            <FormatListNumbered />
          </button>
        )}
        {heading && <Heading currentHeadingLevel={currentHeadingLevel} />}
      </Toolbar>
    </div>
  );
};
